import { useEffect } from 'react'
import { ErrorBoundary } from '@vp/swan'
import useClient from '../../../hooks/useClient'
import type { HeaderLink } from '../../../types/site-header'
import LinkWithIcon from './LinkWithIcon'
import { useAuth } from '../../../hooks/useAuth'
import { MountIds } from '../../../constants/hydration'

type MyProjectsProps = {
  myProjectsLink: HeaderLink
  isRedesignedSignInButtonEnabled: boolean
  isLoggedInSsr: boolean
}

/**
 * Single source of truth for whether the MyProjects entry should render on SSR.
 * Used both by this component and by its parent to avoid emitting an empty <li>.
 */
export const shouldRenderMyProjectsSsr = (myProjectsLink: HeaderLink | undefined, isRedesignedSignInButtonEnabled: boolean, isLoggedInSsr: boolean): boolean =>
  Boolean(myProjectsLink) && !(isRedesignedSignInButtonEnabled && !isLoggedInSsr)

export default function MyProjects({ myProjectsLink, isRedesignedSignInButtonEnabled, isLoggedInSsr }: MyProjectsProps) {
  const isClient = useClient()

  if (!myProjectsLink) return null

  const ssrProjectComponent = (
    <LinkWithIcon href={myProjectsLink.url} iconType="myProjects" label={myProjectsLink.text} analyticsId={myProjectsLink.analyticsId || 'myProjects'} />
  )

  if (!isClient) return ssrProjectComponent

  return (
    <ErrorBoundary fallback={ssrProjectComponent}>
      <MyProjectsClient myProjectsLink={myProjectsLink} isRedesignedSignInButtonEnabled={isRedesignedSignInButtonEnabled} isLoggedInSsr={isLoggedInSsr} />
    </ErrorBoundary>
  )
}

const MyProjectsClient: React.FC<MyProjectsProps> = ({ myProjectsLink, isRedesignedSignInButtonEnabled, isLoggedInSsr }) => {
  const { isSignedIn } = useAuth()
  const shouldHide = isRedesignedSignInButtonEnabled && !(isSignedIn ?? isLoggedInSsr)

  useEffect(() => {
    // Keep parent <li> visibility in sync with client-side auth state.
    // SSR may have hidden it (logged-out) but the client can resolve to logged-in,
    // in which case we must also un-hide it — and vice versa.
    const parentLi = document.getElementById(MountIds.MY_PROJECTS)
    if (parentLi) {
      parentLi.hidden = shouldHide
    }
  }, [shouldHide])

  if (shouldHide) return null

  return <LinkWithIcon href={myProjectsLink.url} iconType="myProjects" label={myProjectsLink.text} analyticsId={myProjectsLink.analyticsId || 'myProjects'} />
}
